export const identityBadgeTypes = {
  seekerGenesis: "seeker_genesis",
  sagaGenesis: "saga_genesis",
  skr: "skr"
};

const identityBadgeDefinitions = [
  { key: "hasSeekerGenesis", label: "Seeker Genesis", type: identityBadgeTypes.seekerGenesis },
  { key: "hasSagaGenesis", label: "Saga Genesis", type: identityBadgeTypes.sagaGenesis },
  { key: "hasSkr", label: ".skr", type: identityBadgeTypes.skr }
];

export function buildIdentityBadges(verification = null) {
  if (!verification || typeof verification !== "object") return [];
  return identityBadgeDefinitions
    .filter((badge) => Boolean(readBooleanField(verification, badge.key)))
    .map((badge) => ({ type: badge.type, label: badge.label }));
}

export function hasGenesisIdentityBadge(verification = null) {
  return Boolean(
    readBooleanField(verification, "hasSeekerGenesis") ||
      readBooleanField(verification, "hasSagaGenesis")
  );
}

export function normalizeSeekerUserFilter(value = "all") {
  const normalized = String(value || "all").trim().toLowerCase();
  if (["all", "seeker", "saga", "both", "general"].includes(normalized)) return normalized;
  return "";
}

export function seekerUserMatchesFilter(verification = null, filter = "all") {
  const normalized = normalizeSeekerUserFilter(filter);
  if (!normalized) return false;
  const hasSeeker = Boolean(readBooleanField(verification, "hasSeekerGenesis"));
  const hasSaga = Boolean(readBooleanField(verification, "hasSagaGenesis"));
  const hasSkr = Boolean(readBooleanField(verification, "hasSkr"));
  if (normalized === "all") return hasSeeker || hasSaga || hasSkr;
  if (normalized === "seeker") return hasSeeker;
  if (normalized === "saga") return hasSaga;
  if (normalized === "both") return hasSeeker && hasSaga;
  return !hasSeeker && !hasSaga && hasSkr;
}

export function buildMarketProvenanceSummary(votes = [], verificationByWallet = new Map()) {
  const counts = {
    both: 0,
    general: 0,
    saga: 0,
    seeker: 0,
    unknown: 0
  };

  for (const vote of Array.isArray(votes) ? votes : []) {
    const walletAddress = voteWalletAddress(vote);
    const verification = walletAddress ? verificationByWallet.get(walletAddress) : null;
    if (!verification) {
      counts.unknown += 1;
      continue;
    }

    const hasSeeker = Boolean(readBooleanField(verification, "hasSeekerGenesis"));
    const hasSaga = Boolean(readBooleanField(verification, "hasSagaGenesis"));
    if (hasSeeker && hasSaga) {
      counts.both += 1;
    } else if (hasSeeker) {
      counts.seeker += 1;
    } else if (hasSaga) {
      counts.saga += 1;
    } else {
      counts.general += 1;
    }
  }

  return buildMarketProvenanceSummaryFromCounts(counts);
}

export function buildMarketProvenanceSummaryFromCounts(inputCounts = {}) {
  const counts = {
    both: Number(inputCounts.both || inputCounts.bothVotes || inputCounts.both_votes || 0) || 0,
    general: Number(inputCounts.general || inputCounts.generalVotes || inputCounts.general_votes || 0) || 0,
    saga: Number(inputCounts.saga || inputCounts.sagaVotes || inputCounts.saga_votes || 0) || 0,
    seeker: Number(inputCounts.seeker || inputCounts.seekerVotes || inputCounts.seeker_votes || 0) || 0,
    unknown: Number(inputCounts.unknown || inputCounts.unknownVotes || inputCounts.unknown_votes || 0) || 0
  };
  const totalVotes = counts.both + counts.general + counts.saga + counts.seeker + counts.unknown;
  return {
    bothVotes: counts.both,
    generalVotes: counts.general,
    sagaVotes: counts.saga,
    seekerVotes: counts.seeker,
    totalVotes,
    unknownVotes: counts.unknown,
    percentages: roundedPercentages(counts, totalVotes)
  };
}

function roundedPercentages(counts, total) {
  if (!total) {
    return { both: 0, general: 0, saga: 0, seeker: 0, unknown: 0 };
  }

  const entries = ["both", "general", "saga", "seeker", "unknown"].map((key, index) => {
    const exact = (counts[key] / total) * 100;
    const floor = Math.floor(exact);
    return { key, index, value: floor, remainder: exact - floor };
  });
  let remaining = 100 - entries.reduce((sum, entry) => sum + entry.value, 0);
  entries
    .slice()
    .sort((left, right) => right.remainder - left.remainder || left.index - right.index)
    .forEach((entry) => {
      if (remaining <= 0) return;
      entry.value += 1;
      remaining -= 1;
    });

  return Object.fromEntries(entries.map((entry) => [entry.key, entry.value]));
}

function readBooleanField(value, camelKey) {
  const snakeKey = camelKey.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  return value?.[camelKey] ?? value?.[snakeKey];
}

function voteWalletAddress(vote = {}) {
  return String(
    vote.user_id ||
      vote.userId ||
      vote.user_wallet ||
      vote.userWallet ||
      vote.wallet_address ||
      vote.walletAddress ||
      ""
  ).trim();
}
